/* ==========================================================================
   Bolt Pattern Calculator
   Self-contained. Loaded only on this calculator's page.
   No shared dependencies.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.querySelector('[data-pc-bpc]');
  if (!root) return;

  var MM_PER_INCH = 25.4;

  /* Patterns that actually exist in volume, with a note on where they turn up. */
  var COMMON = [
    { n: 4, pcd: 100,   note: 'Civic, Corolla, Fiesta, Mini' },
    { n: 4, pcd: 108,   note: 'Focus, older Ford and PSA' },
    { n: 4, pcd: 114.3, note: 'Older Japanese and Korean compacts' },
    { n: 5, pcd: 100,   note: 'Subaru, VW, older Toyota' },
    { n: 5, pcd: 108,   note: 'Ford, Volvo, Jaguar, Land Rover' },
    { n: 5, pcd: 112,   note: 'VW Group, Mercedes-Benz' },
    { n: 5, pcd: 114.3, note: 'Very common: Honda, Toyota, Nissan, Mazda, Mustang, Jeep' },
    { n: 5, pcd: 115,   note: 'GM front drive cars' },
    { n: 5, pcd: 120,   note: 'BMW, older Land Rover, Camaro' },
    { n: 5, pcd: 120.7, note: 'Classic GM and Corvette, 4.75 inch' },
    { n: 5, pcd: 127,   note: 'Jeep JK, older GM trucks, 5 inch' },
    { n: 5, pcd: 139.7, note: 'Trucks and 4x4s, 5.5 inch' },
    { n: 6, pcd: 114.3, note: 'Nissan Frontier, Ranger' },
    { n: 6, pcd: 120,   note: 'Colorado, Canyon, GM midsize trucks' },
    { n: 6, pcd: 127,   note: 'Trailblazer, older GM SUVs' },
    { n: 6, pcd: 135,   note: 'F-150, Expedition' },
    { n: 6, pcd: 139.7, note: 'Tacoma, Tundra, Silverado, 5.5 inch' },
    { n: 8, pcd: 165.1, note: 'Heavy duty GM and Dodge, 6.5 inch' },
    { n: 8, pcd: 170,   note: 'Super Duty Ford' },
    { n: 8, pcd: 180,   note: 'Newer heavy duty GM' },
    { n: 8, pcd: 200,   note: 'Newer Super Duty Ford' }
  ];
  var MATCH_TOLERANCE_MM = 2.0;

  /* ====================================================================
     compute(input) -> result

     PURE. No DOM, no network, no globals. All calculator logic lives here
     so it can be reasoned about and tested in isolation.
     ==================================================================== */
  function compute(input) {
    var lugs = Math.round(input.lugs);
    if (!isFinite(lugs) || lugs < 3) {
      return { ok: false, errors: ['Enter the number of lugs.'] };
    }
    var measured = input.unit === 'in' ? input.measured * MM_PER_INCH : input.measured;
    if (!isFinite(measured) || measured <= 0) {
      return { ok: false, errors: ['Enter the distance you measured.'] };
    }

    var pcd;
    if (input.method === 'pcd') {
      pcd = measured;
    } else if (input.method === 'opposite') {
      if (lugs % 2 !== 0) {
        return { ok: false, errors: ['An odd number of lugs has no directly opposite stud. Measure two neighbouring studs instead.'] };
      }
      pcd = measured;
    } else {
      /* Chord between neighbouring studs: pcd = chord / sin(180/n). */
      pcd = measured / Math.sin(Math.PI / lugs);
    }

    var best = null, bestGap = Infinity;
    COMMON.forEach(function (c) {
      if (c.n !== lugs) return;
      var gap = Math.abs(c.pcd - pcd);
      if (gap < bestGap) { best = c; bestGap = gap; }
    });

    var status;
    if (best && bestGap <= MATCH_TOLERANCE_MM) {
      status = { key: 'good', label: lugs + 'x' + num(best.pcd, 1),
                 note: best.note + '. Your measurement works out to ' + num(pcd, 1) + 'mm.' };
    } else if (best) {
      status = { key: 'monitor', label: lugs + 'x' + num(pcd, 1),
                 note: 'No exact match. Nearest common pattern is ' + lugs + 'x' + num(best.pcd, 1)
                     + ', ' + num(bestGap, 1) + 'mm away. Re-measure carefully before buying.' };
    } else {
      status = { key: 'monitor', label: lugs + 'x' + num(pcd, 1),
                 note: 'Not a pattern we list for that lug count. Double check the measurement.' };
    }

    var rows = [
      { head: 'Result' },
      { label: 'Bolt circle', value: num(pcd, 2) + ' mm' },
      { label: 'In inches', value: num(pcd / MM_PER_INCH, 3) + ' in' },
      { label: 'Written as', value: lugs + 'x' + num(pcd, 1) },
      { label: 'Stud spacing', value: num(pcd * Math.sin(Math.PI / lugs), 2) + ' mm' }
    ];
    if (isFinite(input.bore) && input.bore > 0) {
      rows.push({ head: 'Hub bore' });
      rows.push({ label: 'Bore', value: num(input.bore, 1) + ' mm' });
      rows.push({ label: 'Fitment', value: 'Wheel bore must be equal or larger' });
    }
    rows.push({ head: 'Common ' + lugs + ' lug patterns' });
    COMMON.forEach(function (c) {
      if (c.n === lugs) rows.push({ label: lugs + 'x' + num(c.pcd, 1), value: c.note });
    });

    var warnings = [];
    if (best && bestGap > 0.5 && bestGap <= MATCH_TOLERANCE_MM) {
      warnings.push('Close to ' + lugs + 'x' + num(best.pcd, 1) + ' but not exact. Patterns like 5x112 and 5x114.3 are only 2mm apart and are not interchangeable, so measure twice.');
    }

    return {
      ok: true, errors: [], warnings: warnings, status: status,
      figures: [
        { label: 'Bolt circle diameter', value: num(pcd, 1) + ' mm',
          sub: num(pcd / MM_PER_INCH, 3) + ' inches.' },
        { label: 'Pattern', value: lugs + 'x' + num(best && bestGap <= MATCH_TOLERANCE_MM ? best.pcd : pcd, 1),
          sub: best && bestGap <= MATCH_TOLERANCE_MM ? best.note : 'No common pattern matched.' }
      ],
      rows: rows
    };
  }

  /* ====================================================================
     Formatting
     ==================================================================== */
  function comma(n) {
    return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function num(n, dp) {
    if (!isFinite(n)) return '-';
    var v = Number(n).toFixed(dp == null ? 2 : dp);
    if (dp !== 0 && v.indexOf('.') > -1) v = v.replace(/\.?0+$/, '');
    var parts = v.split('.');
    return comma(parts[0]) + (parts[1] ? '.' + parts[1] : '');
  }

  function signed(n, dp) {
    if (!isFinite(n)) return '-';
    return (n > 0 ? '+' : '') + num(n, dp);
  }

  /* ====================================================================
     DOM wiring
     ==================================================================== */
  var FIELDS = [
      {
          "name": "lugs",
          "key": "n",
          "type": "number"
      },
      {
          "name": "measured",
          "key": "m",
          "type": "number"
      },
      {
          "name": "unit",
          "key": "u",
          "type": "select"
      },
      {
          "name": "method",
          "key": "me",
          "type": "select"
      },
      {
          "name": "bore",
          "key": "b",
          "type": "number"
      }
  ];

  var els = {
    error:    root.querySelector('[data-r-error]'),
    status:   root.querySelector('[data-r-status]'),
    statusLbl:root.querySelector('[data-r-status-label]'),
    statusVal:root.querySelector('[data-r-status-value]'),
    statusNote:root.querySelector('[data-r-status-note]'),
    figures:  root.querySelector('[data-r-figures]'),
    rows:     root.querySelector('[data-r-rows]'),
    warnings: root.querySelector('[data-r-warnings]')
  };

  var STATUS_CLASSES = ['is-good','is-monitor','is-soon','is-now','is-critical'];

  function field(name) { return root.querySelector('[data-f="' + name + '"]'); }

  function readInput() {
    var o = {};
    FIELDS.forEach(function (f) {
      var el = field(f.name);
      if (!el) return;
      if (f.type === 'checkbox') o[f.name] = el.checked;
      else if (f.type === 'number') o[f.name] = parseFloat(el.value);
      else o[f.name] = el.value;
    });
    return o;
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
  }

  function renderFigures(figs) {
    els.figures.innerHTML = (figs || []).map(function (f) {
      return '<div class="pc-bpc__figure">'
        + '<div class="pc-bpc__figure-label">' + esc(f.label) + '</div>'
        + '<div class="pc-bpc__figure-value' + (f.muted ? ' is-muted' : '') + '">'
        + esc(f.value) + '</div>'
        + (f.sub ? '<div class="pc-bpc__figure-sub">' + esc(f.sub) + '</div>' : '')
        + '</div>';
    }).join('');
  }

  function renderRows(rows) {
    if (!els.rows) return;
    els.rows.innerHTML = (rows || []).map(function (r) {
      if (r.head) {
        return '<div class="pc-bpc__row pc-bpc__rows-head">' + esc(r.head) + '</div>';
      }
      return '<div class="pc-bpc__row">'
        + '<span class="pc-bpc__row-label">' + esc(r.label) + '</span>'
        + '<span class="pc-bpc__row-value">' + esc(r.value) + '</span>'
        + '</div>';
    }).join('');
  }

  function clearResult(message) {
    if (els.status) {
      STATUS_CLASSES.forEach(function (c) { els.status.classList.remove(c); });
      els.statusVal.textContent  = 'Waiting for your numbers';
      els.statusNote.textContent = message || '';
    }
    els.figures.innerHTML = '';
    if (els.rows) els.rows.innerHTML = '';
    if (els.warnings) els.warnings.style.display = 'none';
  }

  function render() {
    var input  = readInput();
    var result = compute(input);

    if (!result.ok) {
      els.error.textContent = result.errors[0];
      els.error.classList.add('is-shown');
      clearResult(result.errors[0]);
      return;
    }
    els.error.classList.remove('is-shown');

    if (els.status) {
      STATUS_CLASSES.forEach(function (c) { els.status.classList.remove(c); });
      if (result.status) {
        els.status.classList.add('is-' + result.status.key);
        els.statusVal.textContent  = result.status.label;
        els.statusNote.textContent = result.status.note || '';
      } else {
        els.statusVal.textContent  = '';
        els.statusNote.textContent = '';
      }
    }

    renderFigures(result.figures);
    renderRows(result.rows);

    if (els.warnings) {
      if (result.warnings && result.warnings.length) {
        els.warnings.textContent = result.warnings[0];
        els.warnings.style.display = '';
      } else {
        els.warnings.style.display = 'none';
      }
    }

    writePermalink(input);
  }

  /* ====================================================================
     Shareable result permalinks
     ==================================================================== */
  function writePermalink(input) {
    if (!window.history || !window.history.replaceState) return;
    var q = FIELDS.map(function (f) {
      var v = input[f.name];
      if (f.type === 'checkbox') return v ? f.key + '=1' : '';
      if (v == null || v !== v || v === '') return '';
      return f.key + '=' + encodeURIComponent(v);
    }).filter(Boolean).join('&');
    try {
      window.history.replaceState(null, '', window.location.pathname + (q ? '?' + q : ''));
    } catch (e) {}
  }

  function applyPermalink() {
    if (!window.URLSearchParams) return;
    var p = new URLSearchParams(window.location.search);
    var any = false;
    FIELDS.forEach(function (f) {
      if (!p.has(f.key)) return;
      var el = field(f.name);
      if (!el) return;
      if (f.type === 'checkbox') el.checked = p.get(f.key) === '1';
      else el.value = p.get(f.key);
      any = true;
    });
    return any;
  }

  /* ====================================================================
     Init
     ==================================================================== */
  ['input', 'change'].forEach(function (evt) {
    root.addEventListener(evt, function (e) {
      if (e.target.closest('[data-r-form]')) render();
    });
  });

  applyPermalink();
  render();

  /* Exposed for testing and debugging only. */
  window.PC_BPC = { compute: compute };
})();
